/**
 * The editor's view of its site's design system: loads the server-authorized
 * release (never whatever the document names), asks a collaborator before
 * running a release they haven't agreed to, and disposes the sandbox of a
 * release that's been replaced or left.
 *
 * Local drafts have no server pointer, so they're Duro.
 */
import { useCallback, useEffect, useState } from "react";

import { browserDsDeps } from "./browserDeps.js";
import { hasConsented, recordConsent } from "./consent.js";
import {
  loadActiveDesignSystem,
  type ActiveDesignSystem,
  type DsDeps,
} from "./designSystemFlows.js";

export type DesignSystemState =
  | { readonly kind: "loading" }
  | ActiveDesignSystem;

export interface UseActiveDesignSystem {
  readonly state: DesignSystemState;
  /** Agree to run the release awaiting consent, then load it. */
  readonly allow: () => void;
  /** Read the server's pointer again (after installing or reverting). */
  readonly reload: () => void;
}

export function useActiveDesignSystem(
  /** The cloud site; null for a local draft. */
  siteId: string | null,
  opts: {
    readonly isOwner: boolean;
    /** Tests inject fakes; the editor uses the real ones. */
    readonly deps?: (siteId: string) => DsDeps;
  },
): UseActiveDesignSystem {
  const { isOwner, deps = browserDsDeps } = opts;
  const [state, setState] = useState<DesignSystemState>(
    siteId ? { kind: "loading" } : { kind: "duro" },
  );
  const [generation, setGeneration] = useState(0);

  useEffect(() => {
    if (!siteId) {
      setState({ kind: "duro" });
      return;
    }
    let live = true;
    let loaded: ActiveDesignSystem | null = null;
    setState({ kind: "loading" });
    loadActiveDesignSystem(
      deps(siteId),
      (sha) => isOwner || hasConsented(siteId, sha),
    ).then(
      (ds) => {
        loaded = ds;
        if (live) setState(ds);
        else if (ds.kind === "custom") ds.sandbox.dispose();
      },
      (e: unknown) => {
        if (live)
          setState({
            kind: "unavailable",
            reason: e instanceof Error ? e.message : String(e),
          });
      },
    );
    return () => {
      live = false;
      if (loaded?.kind === "custom") loaded.sandbox.dispose();
    };
    // `deps` is a factory the caller passes once; a new identity each render
    // must not reload the design system.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteId, isOwner, generation]);

  const reload = useCallback(() => setGeneration((g) => g + 1), []);
  const allow = useCallback(() => {
    if (siteId && state.kind === "needs-consent") {
      recordConsent(siteId, state.manifestSha);
      reload();
    }
  }, [siteId, state, reload]);

  return { state, allow, reload };
}
